// ────────── Inline lucide-style icons — from Claude Design prototype ──────────
import React from 'react';

interface IconProps {
  size?: number;
  sw?: number;
  fill?: string;
  style?: React.CSSProperties;
  className?: string;
}

const Icon: React.FC<IconProps & { d?: string; children?: React.ReactNode }> = ({
  d, size = 20, sw = 1.75, fill = 'none', children, style, className,
}) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
    fill={fill} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
    style={style} className={className}>
    {d ? <path d={d} /> : children}
  </svg>
);

export const IconSearch: React.FC<IconProps> = (p) => (
  <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></Icon>
);

export const IconCompass: React.FC<IconProps> = (p) => (
  <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M14.4 9.6 9.6 14.4l-1.2 4.8 4.8-1.2 4.8-4.8z" /></Icon>
);

export const IconBell: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M6 8a6 6 0 1112 0c0 7 3 9 3 9H3s3-2 3-9z" /><path d="M10 21a2 2 0 004 0" /></Icon>
);

export const IconPlane: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M17.8 19.2L16 11l3.5-3.5a2.1 2.1 0 10-3-3L13 8 4.8 6.2a.5.5 0 00-.5.8l3.6 3.7-2.4 2.4-2-.4-1.5 1.5 3.4 1.7 1.7 3.4 1.5-1.5-.4-2 2.4-2.4 3.7 3.6a.5.5 0 00.8-.5z" /></Icon>
);

export const IconTrain: React.FC<IconProps> = (p) => (
  <Icon {...p}><rect x="4" y="3" width="16" height="16" rx="2" /><path d="M4 11h16M8 15h.01M16 15h.01M8 19l-2 3M16 19l2 3" /></Icon>
);

export const IconBuilding: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M3 21V7l9-4 9 4v14" /><path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" /></Icon>
);

export const IconCalendar: React.FC<IconProps> = (p) => (
  <Icon {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" /></Icon>
);

export const IconUsers: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 00-3-3.9M16 3.1A4 4 0 0116 11" /></Icon>
);

export const IconMapPin: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1116 0z" /><circle cx="12" cy="10" r="3" /></Icon>
);

export const IconArrow: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M5 12h14M13 5l7 7-7 7" /></Icon>
);

export const IconChevDown: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M6 9l6 6 6-6" /></Icon>
);

export const IconChevR: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M9 6l6 6-6 6" /></Icon>
);

export const IconChevL: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M15 6l-6 6 6 6" /></Icon>
);

export const IconX: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M18 6L6 18M6 6l12 12" /></Icon>
);

export const IconPlus: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M12 5v14M5 12h14" /></Icon>
);

export const IconStar: React.FC<IconProps> = (p) => (
  <Icon {...p} fill="currentColor" sw={0}><path d="M12 2.5l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 18.4l-6.2 3.2L7 14.7 2 9.8l6.9-1L12 2.5z" /></Icon>
);

export const IconSliders: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></Icon>
);

export const IconSparkle: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /><circle cx="12" cy="12" r="3" /></Icon>
);

export const IconArrUp: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M12 19V5M5 12l7-7 7 7" /></Icon>
);

export const IconArrDn: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M12 5v14M5 12l7 7 7-7" /></Icon>
);

export const IconArrFlat: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M5 12h14M15 8l4 4-4 4" /></Icon>
);

export const IconPause: React.FC<IconProps> = (p) => (
  <Icon {...p}><rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" strokeWidth={0} /><rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" strokeWidth={0} /></Icon>
);

export const IconEdit: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M12 20h9M16.5 3.5a2.1 2.1 0 113 3L7 19l-4 1 1-4z" /></Icon>
);

export const IconTrash: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></Icon>
);

export const IconClock: React.FC<IconProps> = (p) => (
  <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>
);

export const IconWallet: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M3 7v12a2 2 0 002 2h14a2 2 0 002-2v-9a2 2 0 00-2-2H5a2 2 0 110-4h13" /><circle cx="17" cy="14" r="1" fill="currentColor" /></Icon>
);

export const IconShield: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M12 2l8 4v6c0 5-4 9-8 10-4-1-8-5-8-10V6l8-4z" /></Icon>
);

export const IconInfo: React.FC<IconProps> = (p) => (
  <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></Icon>
);

export const IconSwap: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M7 16h13M16 12l4 4-4 4M17 8H4M8 4 4 8l4 4" /></Icon>
);

export const IconHeart: React.FC<IconProps> = (p) => (
  <Icon {...p}><path d="M20.8 5.6a5 5 0 00-7.1 0L12 7.3l-1.7-1.7a5 5 0 10-7.1 7.1l1.7 1.7L12 21.5l7.1-7.1 1.7-1.7a5 5 0 000-7.1z" /></Icon>
);
